import { Link, useParams } from 'react-router-dom'
import { useMemo, useState } from 'react'
import Header from '../header'
import {
    useGetDevice,
    useGetDeviceErrors,
} from '../../../../api/routes/devices'
import { Badge } from '../../../../components/ui/badge'
import { ScrollArea } from '../../../../components/ui/scroll-area'
import {
    Clock,
    AlertCircle,
    AlertTriangle,
    History,
    TrendingUp,
    Filter,
    ChevronRight,
    Search,
} from 'lucide-react'
import moment from 'moment'
import { Button } from '../../../../components/ui/button'

const ErrorCard = ({
    error,
    deviceId,
}: {
    error: Record<string, Record<string, string>>
    deviceId: string
}) => {
    if (!error?.error || error?.error === null) {
        return null
    }

    const timestamp = parseInt(error.deviceData.timestamp)
    const formattedDate = moment(timestamp).format('MMM D, h:mm:ss A')
    const relativeTime = moment(timestamp).fromNow()

    return (
        <Link to={`/devices/${deviceId}/errors/${error.deviceData.id}`}>
            <div className="rounded-lg border bg-card p-4 sm:p-5 mb-2 hover:border-foreground/20 transition-colors duration-150 cursor-pointer group">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                            <Badge
                                variant="destructive"
                                className="h-5 text-xs flex-shrink-0"
                            >
                                {error.error?.code}
                            </Badge>
                            <span className="text-sm font-medium text-foreground truncate">
                                {error.error.title}
                            </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                            {error.error.description}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                            <p className="text-xs font-mono text-muted-foreground">
                                {formattedDate}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {relativeTime}
                            </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                </div>
            </div>
        </Link>
    )
}

const ErrorStatsCard = ({ title, value, description, color }) => (
    <div className="rounded-lg border bg-card p-4 sm:p-5">
        <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {title}
                </p>
                <p className="text-lg sm:text-2xl font-semibold text-foreground mt-1">
                    {value}
                </p>
                {description && (
                    <p className="text-xs text-muted-foreground mt-1">
                        {description}
                    </p>
                )}
            </div>
        </div>
    </div>
)

const SingleDeviceErrorsRoute = () => {
    const { id: deviceId } = useParams()
    const { data: device, isLoading: deviceLoading } = useGetDevice(
        deviceId as string
    )
    const { data: errors, isLoading: errorsLoading } = useGetDeviceErrors(
        deviceId as string
    )
    const [filterCode, setFilterCode] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')

    const errorBreakdown = useMemo(() => {
        if (!errors?.data) return []
        const groups: Record<
            string,
            {
                code: string
                title: string
                count: number
                lastSeen: number
                latestErrorId: number
            }
        > = {}
        errors.data.forEach((e) => {
            if (!e.error) return
            const code = e.error.code
            if (!groups[code]) {
                groups[code] = {
                    code,
                    title: e.error.title,
                    count: 0,
                    lastSeen: parseInt(e.deviceData.timestamp),
                    latestErrorId: e.deviceData.id,
                }
            }
            groups[code].count++
            const ts = parseInt(e.deviceData.timestamp)
            if (ts > groups[code].lastSeen) {
                groups[code].lastSeen = ts
                groups[code].latestErrorId = e.deviceData.id
            }
        })
        return Object.values(groups).sort((a, b) => b.count - a.count)
    }, [errors])

    const filteredErrors = useMemo(() => {
        if (!errors?.data) return []
        let filtered = errors.data
        if (filterCode) {
            filtered = filtered.filter((e) => e.error?.code === filterCode)
        }
        if (searchQuery) {
            const q = searchQuery.toLowerCase()
            filtered = filtered.filter(
                (e) =>
                    e.error?.title?.toLowerCase().includes(q) ||
                    e.error?.description?.toLowerCase().includes(q) ||
                    e.error?.code?.toLowerCase().includes(q)
            )
        }
        return filtered
    }, [errors, filterCode, searchQuery])

    if (deviceLoading || errorsLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-48 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-96 bg-muted rounded-lg" />
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="h-24 bg-muted rounded-lg" />
                        <div className="h-24 bg-muted rounded-lg" />
                        <div className="h-24 bg-muted rounded-lg" />
                        <div className="h-24 bg-muted rounded-lg" />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <div className="h-64 bg-muted rounded-lg lg:col-span-2" />
                        <div className="h-64 bg-muted rounded-lg" />
                    </div>
                </div>
            </div>
        )
    }

    if (!device || !deviceId) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <Header
                    deviceId={deviceId}
                    name=""
                    businessId={device?.business?.id}
                />
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground mb-1">
                        Device Not Found
                    </p>
                    <p className="text-xs text-muted-foreground">
                        Please check the device ID and try again.
                    </p>
                </div>
            </div>
        )
    }

    const hasErrors = errors && errors?.data?.length > 0

    const latestErrorTime = hasErrors
        ? moment(parseInt(errors.data[0].deviceData.timestamp)).format(
              'MMM D, h:mm A'
          )
        : 'None'

    const uniqueErrorCodes = hasErrors ? errorBreakdown.length : 0

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <Header
                deviceId={deviceId}
                name={device?.name}
                businessId={device?.business?.id}
            />

            <div className="space-y-6">
                {hasErrors ? (
                    <>
                        {/* Stats */}
                        <div>
                            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                                Error Statistics
                            </h2>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <ErrorStatsCard
                                    title="Total Errors"
                                    value={errors.details.totalErrors}
                                    description="All time"
                                    color="bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400"
                                />
                                <ErrorStatsCard
                                    title="Past Week"
                                    value={errors.details.pastWeekErrors}
                                    description="Last 7 days"
                                    color="bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400"
                                />
                                <ErrorStatsCard
                                    title="Past Day"
                                    value={errors.details.pastDayErrors}
                                    description="Last 24 hours"
                                    color="bg-yellow-50 text-yellow-600 dark:bg-yellow-950 dark:text-yellow-400"
                                />
                                <ErrorStatsCard
                                    title="Unique Errors"
                                    value={uniqueErrorCodes}
                                    description={`Latest: ${latestErrorTime}`}
                                    color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
                                />
                            </div>
                        </div>

                        {/* Error Breakdown + Error List */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                            {/* Error Breakdown */}
                            <div>
                                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                                    Error Breakdown
                                </h2>
                                <div className="rounded-lg border bg-card p-4 sm:p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="text-xs text-muted-foreground">
                                            {errorBreakdown.length} unique error
                                            {errorBreakdown.length !== 1
                                                ? 's'
                                                : ''}
                                        </p>
                                        {filterCode && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 text-xs px-2"
                                                onClick={() =>
                                                    setFilterCode(null)
                                                }
                                            >
                                                Clear filter
                                            </Button>
                                        )}
                                    </div>
                                    <ScrollArea className="h-[400px]">
                                        {errorBreakdown.map((group) => (
                                            <div
                                                key={group.code}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    setFilterCode(
                                                        filterCode ===
                                                            group.code
                                                            ? null
                                                            : group.code
                                                    )
                                                }}
                                            >
                                                <div
                                                    className={`flex items-center justify-between py-3 border-b border-border/50 last:border-0 -mx-4 px-4 transition-colors cursor-pointer ${
                                                        filterCode ===
                                                        group.code
                                                            ? 'bg-muted'
                                                            : 'hover:bg-muted/50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                                                        <Badge
                                                            variant={
                                                                filterCode ===
                                                                group.code
                                                                    ? 'default'
                                                                    : 'destructive'
                                                            }
                                                            className="h-5 text-xs flex-shrink-0"
                                                        >
                                                            {group.code}
                                                        </Badge>
                                                        <div className="min-w-0 overflow-hidden">
                                                            <p className="text-sm font-medium text-foreground truncate">
                                                                {group.title}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                Last{' '}
                                                                {moment(
                                                                    group.lastSeen
                                                                ).fromNow()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                        <span className="text-sm font-semibold text-foreground tabular-nums">
                                                            {group.count}x
                                                        </span>
                                                        {filterCode ===
                                                            group.code && (
                                                            <Filter className="h-3 w-3 text-foreground" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </ScrollArea>
                                </div>
                            </div>

                            {/* Error List */}
                            <div className="lg:col-span-2">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                                        {filterCode
                                            ? `Errors: ${filterCode}`
                                            : 'All Errors'}
                                    </h2>
                                    <span className="text-xs text-muted-foreground">
                                        {filteredErrors.length} error
                                        {filteredErrors.length !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                {/* Search */}
                                <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Search errors..."
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                    />
                                </div>

                                <ScrollArea className="h-[440px]">
                                    {filteredErrors.length > 0 ? (
                                        filteredErrors.map((error) => (
                                            <ErrorCard
                                                key={error.deviceData.id}
                                                error={error}
                                                deviceId={deviceId}
                                            />
                                        ))
                                    ) : (
                                        <div className="rounded-lg border border-dashed p-8 text-center">
                                            <p className="text-sm text-muted-foreground">
                                                No errors match your search.
                                            </p>
                                        </div>
                                    )}
                                </ScrollArea>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="rounded-lg border border-dashed p-12 text-center">
                        <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm font-medium text-foreground mb-1">
                            No Errors
                        </p>
                        <p className="text-xs text-muted-foreground">
                            No errors have been recorded for this device.
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}

export { SingleDeviceErrorsRoute }
